import { useMemo, useState } from "react";
import { MultiAutocomplete } from "../ds";
import { useUserSearch } from "../hooks/useUserSearch";
import type { UserSearchResult } from "../types/auth";

interface Props {
  value: UserSearchResult[];
  onChange: (value: UserSearchResult[]) => void;
  label?: string;
  disabled?: boolean;
}

// Name plus email, so two colleagues with the same name are
// distinguishable. Used for both the option rows and the selected chips —
// one label function keeps them from drifting apart.
const recipientLabel = (u: UserSearchResult): string =>
  u.email ? `${u.name} · ${u.email}` : u.name;

/**
 * Extra people to copy on the email a workflow step sends. Every action
 * dialog carries one; the picked ids ride along as
 * `params.additional_recipient_user_ids` and the backend adds them to the
 * recipients of the notification that step already sends — so this never
 * grants access, it only widens one email.
 *
 * Options come from the server-side user search, so MUI's own filtering is
 * switched off: the returned list is already the answer to the typed query
 * (which may have matched on employee id, not the displayed label).
 */
export default function RecipientPicker({
  value,
  onChange,
  label = "Also notify (optional)",
  disabled,
}: Props) {
  const [query, setQuery] = useState("");
  const { results, loading } = useUserSearch(query);

  // Already-picked people stay in the option list even once the search
  // text that found them is gone — MUI treats a selected value that isn't
  // an option as invalid, and it keeps every chip de-selectable from the
  // menu rather than only by its delete affordance.
  const options = useMemo(() => {
    const picked = new Set(value.map((u) => u.id));
    return [...value, ...results.filter((r) => !picked.has(r.id))];
  }, [value, results]);

  return (
    <MultiAutocomplete<UserSearchResult>
      label={label}
      placeholder="Search by name or employee ID"
      helperText="They receive the email sent for this step."
      disabled={disabled}
      options={options}
      value={value}
      loading={loading}
      noOptionsText={
        query.trim().length < 2 ? "Type at least 2 characters" : "No matches"
      }
      getOptionLabel={recipientLabel}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      filterOptions={(o) => o}
      onInputChange={setQuery}
      onChange={onChange}
    />
  );
}
